import {NgxNotifications} from '@ngx-notifications/notifications.service';
import {SessionService} from '@core/services/session.service';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {first, map, share, switchMap} from 'rxjs/operators';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Constants} from '@app/app.constants';
import {Router} from '@angular/router';
import {of} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    constructor(private _router: Router,
                private _http: HttpClient,
                private _session: SessionService,
                private _notifications: NgxNotifications,
                @Inject(PLATFORM_ID) private _platformId: object) {
    }

    get accountFromCache() {
        return this._session.user;
    }

    get accountFromServer() {
        this._http.get<Api.User>(Constants.endpoints.me).toPromise()
            .then(user => this._session.setUser(user))
            .catch(_ => _);

        return this.accountFromCache.pipe(
            share()
        );
    }

    async login(email: string, password: string, as: string[], platformId: string = null) {
        try {
            // Wait until the token comes back.
            const {user, token, refresh_token} = await this._http.post<Api.LoginResponse>(Constants.endpoints.login, {
                email,
                password,
                platform_id: platformId
            }).toPromise();

            // Check if the user is the requested type.
            if (!as.includes(user.role)) {
                throw new Error('Invalid role.');
            }

            // Set the tokens.
            this._session.setTokens(token, refresh_token);

            // Save the user to the local storage so we can retrieve him easily.
            this._session.setUser(user);
        } catch (error) {
            // Show an error notification.
            this._notifications.error('The email-password combination is not recognized.');

            // Re-throw the error.
            throw error;
        }
    }

    async register(data: Api.User, platformId: string = null) {
        try {
            // Wait until the token comes back.
            const {user, token, refresh_token} = await this._http.post<Api.LoginResponse>(Constants.endpoints.register, {
                user: {...data, platform_id: platformId}
            }).toPromise();

            // Set the tokens.
            this._session.setTokens(token, refresh_token);

            // Save the user to the local storage so we can retrieve him easily.
            this._session.setUser(user);
        } catch (error) {
            if (!error.error.errors.email) {
                // Show an error notification.
                this._notifications.error('Something went wrong while registering.');
            }

            // Re-throw the error.
            throw error;
        }
    }

    async logout() {
        // Get the account from the cache.
        const account = await this.accountFromCache.pipe(first()).toPromise();

        // Get the account role.
        const role = `${account.role}`;

        // Perform the logout call.
        try {
            // Log the user out.
            await this._http.get(Constants.endpoints.logout, {headers: {Authorization: 'refresh'}})
                .toPromise();
        } catch (error) {
            // Do nothing because the tokens will be deleted anyways.
        }

        // Delete the tokens from the session.
        this._session.deleteTokensAndUser();

        // Return the user role.
        return role;
    }

    async update(data: Partial<Api.User>, showNotification: boolean = false) {
        try {
            // Update the user profile.
            const user = await this._http.put<Api.User>(Constants.endpoints.me, {user: data})
                .toPromise();

            // Save the new user.
            this._session.setUser(user);

            // Notify the user that the update was successful.
            if (showNotification) {
                this._notifications.success('Your profile was successfully updated.');
            }

            return user;
        } catch (error) {
            // Show an error notification.
            if (showNotification) {
                this._notifications.error('Your profile could not be updated.');
            }

            // Rethrow the error.
            throw error;
        }
    }

    async changePassword(data: { current_password: string, password: string }) {
        // Update the user profile.
        const user = await this._http.put<Api.User>(Constants.endpoints.me, {user: data})
            .toPromise();

        // Save the new user.
        this._session.setUser(user);
    }

    async setAvatar(file: File) {
        // Create a form data with the file.
        const formData = new FormData();
        formData.append('avatar', file, file.name);

        try {
            // Wait until the avatar is uploaded.
            const user = await this._http.put<Api.User>(Constants.endpoints.avatar, formData).toPromise();

            // Save the new user object.
            this._session.setUser(user);

            // Notify the user that the update was successful.
            this._notifications.success('Your profile was successfully updated.');

            return user;
        } catch (error) {
            // Show an error notification.
            this._notifications.error('Your profile could not be updated.');
        }
    }

    async delete() {
        try {
            // Wait until the http delete has finished.
            await this._http.delete(Constants.endpoints.me)
                .toPromise();

            // Remove the user from the session.
            this._session.deleteTokensAndUser();

            // Notify the user.
            this._notifications.success('Your account has been successfully deleted.');
        } catch (error) {
            console.log(error);
        }
    }

    async request(email: string, platformId: string = null) {
        // Compose the url.
        const url = `${Constants.endpoints.request}`;
        const extras = platformId ? `?platform_id=${platformId}` : '';

        try {
            // Do the reset API call.
            await this._http.post(url + extras, {email}).toPromise();

            // Notify the user always that the call was successful.
            this._notifications.success('An email has been sent to reset your password.');
        } catch (error) {
            // Throw a new error so that the component can handle it.
            throw error;
        }
    }

    async reset(token: string, password: string, platformId: string = null) {
        // Compose the url.
        const url = `${Constants.endpoints.reset}`;
        const extras = platformId ? `?platform_id=${platformId}` : '';

        try {
            // Do the reset API call.
            await this._http.post(url + extras, {token, password}).toPromise();

            // Notify the user always that the call was successful.
            this._notifications.success('Your password has successfully been changed.');
        } catch (error) {
            // Throw a new error so that the component can handle it.
            this._notifications.error('Your password could not be changed.');
        }
    }

    async subscribe(type: string, paymentMethod: string) {
        if (!isPlatformBrowser(this._platformId)) {
            return null;
        }

        let url = null;

        switch (type) {
            case 'monthly':
                url = Constants.endpoints.subscriptionMonthly;
                break;
            case 'yearly':
            case 'quarterly':
                url = Constants.endpoints.subscriptionOneOff;
                break;
        }

        if (!url) {
            return;
        }

        // Do a POST request to the API.
        const result = await this._http.post<{ status: string, url: string }>(url, {
            redirect_url: window.location.href,
            type: type,
            method: paymentMethod
        }).toPromise();

        // Check if the url was passed.
        if (!result.url) {
            this._notifications.error('Something went wrong.');
            throw new Error('No url.');
        }

        // Assign the new location to the url.
        window.location.assign(result.url);
    }

    async cancelSubscription() {
        try {
            // Do a DELETE request to the API.
            await this._http.delete(Constants.endpoints.subscription).toPromise();

            // Reload the account.
            await this.accountFromServer.pipe(first()).toPromise();
        } catch (error) {
            console.log(error);
        }
    }

    get invoices() {
        return this.accountFromCache.pipe(
            map(account => account && account.id ? account.id : null),
            switchMap(accountId => {
                if (!accountId) {
                    return of([]);
                }

                return this._http.get<Api.Invoice[]>(`${Constants.endpoints.users}/${accountId}/invoices`);
            }),
            share()
        );
    }

    get prices() {
        return this._http.get<{
            price_subscription_monthly: number,
            price_subscription_quarterly: number,
            price_subscription_yearly: number
        }>(Constants.endpoints.prices).pipe(
            share()
        );
    }

}
